import React, { useEffect, useState } from "react";
import { Polyline, Marker, Tooltip } from "react-leaflet";
import axios from "axios";
import { Spin } from "antd";
import {
  createStationaryIcon,
  getRouteDeviation,
} from "../../../../Utils/getMapData";

export const getLabelPosition = (coordinates) => {
  if (!coordinates?.length) return [0, 0];

  const point = coordinates[Math.floor(coordinates.length / 2)];

  return [point[0], point[1]];
};

const getSegmentKey = (segment) => JSON.stringify(segment);

// =========================================================
// FALLBACK ROUTE GENERATION
//
// Only used when fastestRouteCoordinates is NOT available
// from DB.
// =========================================================

const getFastestRoute = async (stopCoordinates) => {
  if (!Array.isArray(stopCoordinates) || stopCoordinates.length < 2) {
    return stopCoordinates || [];
  }

  try {
    const routeCoordinates = [...stopCoordinates];

    // -------------------------------------------------------
    // Round-trip support
    //
    // If the trip doesn't explicitly contain the first
    // station as the final stop, append it so OSRM also
    // calculates the final leg:
    //
    // last stop -> station
    // -------------------------------------------------------

    const firstPoint = routeCoordinates[0];
    const lastPoint = routeCoordinates[routeCoordinates.length - 1];

    const sameStartAndEnd =
      firstPoint &&
      lastPoint &&
      Number(firstPoint[0]) === Number(lastPoint[0]) &&
      Number(firstPoint[1]) === Number(lastPoint[1]);

    if (!sameStartAndEnd) {
      routeCoordinates.push([Number(firstPoint[0]), Number(firstPoint[1])]);
    }

    // -------------------------------------------------------
    // Remove invalid coordinates
    // -------------------------------------------------------

    const validCoordinates = routeCoordinates.filter(
      ([lat, lng]) =>
        Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)),
    );

    if (validCoordinates.length < 2) {
      return stopCoordinates;
    }

    // -------------------------------------------------------
    // Leaflet:
    // [lat, lng]
    //
    // OSRM:
    // [lng, lat]
    // -------------------------------------------------------

    const coordinates = validCoordinates
      .map(([lat, lng]) => `${lng},${lat}`)
      .join(";");

    const response = await axios.get(
      `https://router.project-osrm.org/route/v1/driving/${coordinates}`,
      {
        params: {
          overview: "full",
          geometries: "geojson",
          steps: false,
        },
      },
    );

    const route = response?.data?.routes?.[0]?.geometry?.coordinates;

    if (!Array.isArray(route) || route.length < 2) {
      return stopCoordinates;
    }

    return route.map(([lng, lat]) => [Number(lat), Number(lng)]);
  } catch (error) {
    console.error("Failed to create planned route:", error);

    return stopCoordinates;
  }
};

// =========================================================
// COMPONENT
// =========================================================

const CompletedTripMapComponents = ({
  stopCoordinates = [],
  fastestRouteCoordinates = [],
  actualRoute = [],
  stationaryPoints = [],
}) => {
  const [plannedRoute, setPlannedRoute] = useState([]);

  const [gpsRoute, setGpsRoute] = useState([]);

  const [routeLoading, setRouteLoading] = useState(true);

  // =========================================================
  // PLANNED ROUTE
  //
  // Priority:
  //
  // 1. fastestRouteCoordinates from DB
  // 2. Generate using OSRM as fallback
  //
  // stopCoordinates is NEVER modified.
  // =========================================================

  useEffect(() => {
    let cancelled = false;

    const loadPlannedRoute = async () => {
      setRouteLoading(true);

      // -----------------------------------------------------
      // 1. USE STORED FASTEST ROUTE
      // -----------------------------------------------------

      const hasStoredRoute =
        Array.isArray(fastestRouteCoordinates) &&
        fastestRouteCoordinates.length >= 2;

      if (hasStoredRoute) {
        if (!cancelled) {
          setPlannedRoute(fastestRouteCoordinates);

          setRouteLoading(false);
        }

        return;
      }

      // -----------------------------------------------------
      // 2. FALLBACK TO OSRM
      // -----------------------------------------------------

      try {
        const generatedRoute = await getFastestRoute(stopCoordinates);

        if (cancelled) {
          return;
        }

        setPlannedRoute(generatedRoute || []);
      } catch (error) {
        console.error("Failed to load planned route:", error);

        if (!cancelled) {
          setPlannedRoute(stopCoordinates || []);
        }
      } finally {
        if (!cancelled) {
          setRouteLoading(false);
        }
      }
    };

    loadPlannedRoute();

    return () => {
      cancelled = true;
    };
  }, [stopCoordinates, fastestRouteCoordinates]);

  // =========================================================
  // ACTUAL GPS ROUTE
  //
  // Keep this completely independent from planned route.
  // =========================================================

  useEffect(() => {
    if (!actualRoute?.length) {
      setGpsRoute([]);
      return;
    }

    const normalizedRoute = actualRoute
      .map((point) => {
        const lat = Array.isArray(point)
          ? Number(point[0])
          : Number(point?.lat);

        const lng = Array.isArray(point)
          ? Number(point[1])
          : Number(point?.lng ?? point?.long);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          return null;
        }

        return [lat, lng];
      })
      .filter(Boolean);

    setGpsRoute(normalizedRoute);
  }, [actualRoute]);

  // =========================================================
  // DEVIATION
  //
  // IMPORTANT:
  // Compare GPS against the stored/generated ROAD ROUTE.
  //
  // NOT raw stopCoordinates.
  // =========================================================

  const {
    normalSegments = [],
    plannedSegments = [],
    actualSegments = [],
  } = getRouteDeviation(plannedRoute, gpsRoute, 40);

  // =========================================================
  // LOADING
  // =========================================================

  if (routeLoading) {
    return (
      <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-white/40">
        <Spin size="large" />
      </div>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      {/* =====================================================
          1. ENTIRE BEST / PLANNED ROUTE

          This is now the route stored in DB whenever
          fastestRouteCoordinates exists.
          ===================================================== */}

      {plannedSegments.map(({ coordinates }) => (
        <Polyline
          key={`planned-${getSegmentKey(coordinates)}`}
          positions={coordinates}
          pathOptions={{
            color: "#528587",
            weight: 4,
            dashArray: "4 8",
            lineCap: "round",
            lineJoin: "round",
          }}
        />
      ))}

      {/* =====================================================
          2. NORMAL / TRAVELLED ROUTE
          ===================================================== */}

      {normalSegments.map((segment) => (
        <React.Fragment key={`normal-${getSegmentKey(segment)}`}>
          <Polyline
            positions={segment}
            pathOptions={{
              color: "#085054",
              weight: 8,
              lineCap: "round",
              lineJoin: "round",
            }}
          />

          <Polyline
            positions={segment}
            pathOptions={{
              color: "#AABDBD",
              weight: 5,
              lineCap: "round",
              lineJoin: "round",
            }}
          />
        </React.Fragment>
      ))}

      {/* =====================================================
          3. ACTUAL DEVIATION
          ===================================================== */}

      {actualSegments.map((segment) => (
        <React.Fragment key={`actual-${getSegmentKey(segment)}`}>
          <Polyline
            positions={segment}
            pathOptions={{
              color: "#C84630",
              weight: 8,
              lineCap: "round",
              lineJoin: "round",
            }}
          />

          <Polyline
            positions={segment}
            pathOptions={{
              color: "#F4DAD6",
              weight: 5,
              lineCap: "round",
              lineJoin: "round",
            }}
          />
        </React.Fragment>
      ))}

      {/* =====================================================
          4. STATIONARY POINTS
          ===================================================== */}

      {stationaryPoints?.map((point) => (
        <Marker
          key={`stationary-${point.lat}-${point.long}-${point.duration_seconds}`}
          position={[point.lat, point.long]}
          icon={createStationaryIcon()}
        >
          <Tooltip
            permanent={false}
            direction="top"
            offset={[0, -10]}
            opacity={1}
          >
            <div className="w-[229px] rounded-md p-2">
              <div className="flex justify-between">
                <span className="font-aileron font-normal text-xs text-dark-gray">
                  Idle Time Detected
                </span>

                <span className="rounded-4 bg-light-grey px-2 py-1 font-aileron font-semibold text-xs text-black">
                  {Math.round(point.duration_seconds / 60)} Min
                </span>
              </div>

              <div className="mt-2 flex justify-between">
                <span className="font-aileron font-normal text-xs text-dark-gray">
                  Location
                </span>

                <span className="font-aileron font-semibold text-xs text-black">
                  {Number(point.lat).toFixed(6)},{" "}
                  {Number(point.long).toFixed(6)}
                </span>
              </div>
            </div>
          </Tooltip>
        </Marker>
      ))}
    </>
  );
};

export default CompletedTripMapComponents;
